import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import {
  Bot, Eye, Target, PenTool, BarChart3, Zap, Power, Activity,
  CheckCircle2, Clock, AlertTriangle, TrendingUp, Shield, Play, Loader2,
  Heart, Crosshair, Calculator, Scale, Users, Rocket, FileText,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useNanoCorp } from "@/hooks/useNanoCorp";
import { NanoCorpBriefingDialog } from "@/components/nanocorp/BriefingDialog";
import { AgentDirectives } from "@/components/nanocorp/AgentDirectives";
import { AgentProducts } from "@/components/nanocorp/AgentProducts";
import { AcquisitionPipeline } from "@/components/nanocorp/AcquisitionPipeline";
import { AgentChains } from "@/components/nanocorp/AgentChains";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";

const AGENT_META: Record<string, { name: string; role: string; description: string; icon: React.ElementType; color: string; action: string }> = {
  veilleur: { name: "Veilleur", role: "Détection d'anomalies", description: "Rédige chaque jour un rapport sur vos flux financiers et alertes métier, pour repérer les anomalies.", icon: Eye, color: "hsl(var(--chart-1))", action: "run_scan" },
  qualificateur: { name: "Qualificateur", role: "Scoring & qualification", description: "Rédige un rapport de priorisation de vos prospects et opportunités à partir du CRM.", icon: Target, color: "hsl(var(--chart-2))", action: "run_qualification" },
  redacteur: { name: "Rédacteur", role: "Séquences de prospection", description: "Rédige des brouillons de séquences d'emails par offre. Rien n'est envoyé sans votre validation.", icon: PenTool, color: "hsl(var(--chart-3))", action: "run_redaction" },
  analyste: { name: "Analyste", role: "Briefings stratégiques", description: "Rédige un briefing : performance, risques, opportunités et recommandations.", icon: BarChart3, color: "hsl(var(--chart-4))", action: "run_briefing" },
  prospecteur: { name: "Prospecteur", role: "Acquisition & outreach", description: "Rédige un rapport de pistes de prospection à partir de vos contacts CRM. N'ajoute rien au pipeline et ne contacte personne.", icon: Zap, color: "hsl(var(--chart-5))", action: "run_prospection" },
  nurturing: { name: "Nurturing", role: "Relance & engagement", description: "Propose des idées de relance pour vos prospects tièdes. Aucune relance n'est envoyée.", icon: Heart, color: "hsl(340 80% 55%)", action: "run_nurturing" },
  comptable: { name: "Comptable", role: "Suivi financier & facturation", description: "Rédige un point de suivi financier. Pas de rapprochement bancaire automatique.", icon: Calculator, color: "hsl(170 80% 45%)", action: "run_accounting" },
  juridique: { name: "Juridique", role: "Conformité & veille réglementaire", description: "Rédige une veille générale sur les obligations légales et échéances réglementaires.", icon: Scale, color: "hsl(210 80% 55%)", action: "run_legal" },
  rh: { name: "RH", role: "Recrutement & gestion d'équipe", description: "Rédige des brouillons d'offres d'emploi. Sans équipe aujourd'hui : peu utile.", icon: Users, color: "hsl(30 90% 55%)", action: "run_hr" },
};

const statusConfig = {
  active: { label: "Rapport quotidien", icon: Activity, className: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20" },
  running: { label: "En cours", icon: Clock, className: "bg-amber-500/10 text-amber-500 border-amber-500/20" },
  standby: { label: "Veille", icon: Power, className: "bg-muted text-muted-foreground border-border" },
  disabled: { label: "Désactivé", icon: Power, className: "bg-muted text-muted-foreground border-border opacity-50" },
};

const NanoCorp = () => {
  const { configs, missions, logs, loading, runningAgent, toggleAgent, runAgent, getAgentStats, refreshData } = useNanoCorp();
  const navigate = useNavigate();
  const [briefingContent, setBriefingContent] = useState<string | null>(null);
  const [briefingOpen, setBriefingOpen] = useState(false);

  const getAgentStatus = (agentId: string) => {
    const config = configs.find((c) => c.agent_id === agentId);
    if (!config?.enabled) return "disabled";
    if (runningAgent === agentId) return "running";
    const stats = getAgentStats(agentId);
    if (stats.running > 0) return "running";
    if (stats.completed > 0) return "active";
    return "standby";
  };

  const handleRunAgent = async (agentId: string) => {
    const meta = AGENT_META[agentId];
    if (!meta) return;
    const result = await runAgent(agentId, meta.action);
    if (result?.briefing) {
      setBriefingContent(result.briefing);
      setBriefingOpen(true);
    }
  };

  const [runningAll, setRunningAll] = useState(false);
  const [allResults, setAllResults] = useState<any[]>([]);
  const [latestReports, setLatestReports] = useState<any[]>([]);

  const handleRunAll = async () => {
    setRunningAll(true);
    setAllResults([]);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/nanocorp/run-all`, {
        method: "POST", credentials: "include" as RequestCredentials,
      });
      if (res.ok) {
        const data = await res.json();
        setAllResults(data.results || []);
        setBriefingContent(`**Scan complet terminé**\n\n${data.completed}/${data.total} agents ont complété leur mission.\n\n${(data.results || []).filter((r: any) => r.briefing).map((r: any) => `**${r.agent_id}** :\n${r.briefing}`).join("\n\n---\n\n")}`);
        setBriefingOpen(true);
        refreshData();
      }
    } catch {}
    setRunningAll(false);
  };

  const fetchReports = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/nanocorp/latest-reports`, { credentials: "include" as RequestCredentials });
      if (res.ok) {
        const data = await res.json();
        setLatestReports(data.reports || []);
      }
    } catch {}
  };

  useEffect(() => { fetchReports(); }, [configs]);

  const activeCount = configs.filter((c) => c.enabled).length;
  const totalMissions = missions.filter((m) => m.status === "completed").length;
  const avgSuccess = totalMissions > 0 ? (totalMissions / Math.max(missions.length, 1)) * 100 : 0;

  if (loading) {
    return (
      <SidebarProvider>
        <div className="min-h-screen flex w-full bg-background">
          <DashboardSidebar />
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <DashboardSidebar />
        <div className="flex-1 flex flex-col">
          <header className="h-14 flex items-center border-b border-border px-4 gap-3">
            <SidebarTrigger />
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-violet-600 to-fuchsia-500 flex items-center justify-center">
                <Bot className="h-4 w-4 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-foreground leading-none">NanoCorp</h1>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Autonomous AI System</p>
              </div>
            </div>
            <div className="flex-1" />
            <Button size="sm" className="gap-1.5" onClick={handleRunAll} disabled={runningAll || activeCount === 0} data-testid="run-all-agents-btn">
              {runningAll ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
              {runningAll ? "Analyse en cours..." : "Lancer tous les agents"}
            </Button>
          </header>

          <main className="flex-1 p-4 md:p-6 space-y-6 overflow-auto">
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm text-muted-foreground">
              Les agents NanoCorp rédigent chaque jour des rapports d'aide à la décision à partir de votre CRM et de vos e-mails. Ils n'envoient aucun message et n'écrivent pas dans HubSpot : vous validez chaque action.
            </div>
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card className="border-border/50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                    <Bot className="h-3.5 w-3.5" /> Agents actifs
                  </div>
                  <p className="text-2xl font-bold text-foreground">{activeCount}<span className="text-sm text-muted-foreground font-normal">/{configs.length}</span></p>
                </CardContent>
              </Card>
              <Card className="border-border/50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Missions réalisées
                  </div>
                  <p className="text-2xl font-bold text-foreground">{totalMissions}</p>
                </CardContent>
              </Card>
              <Card className="border-border/50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                    <TrendingUp className="h-3.5 w-3.5" /> Taux de succès
                  </div>
                  <p className="text-2xl font-bold text-foreground">{avgSuccess.toFixed(0)}%</p>
                </CardContent>
              </Card>
              <Card className="border-border/50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                    <Shield className="h-3.5 w-3.5" /> Statut système
                  </div>
                  <p className="text-sm font-semibold text-emerald-500">● Opérationnel</p>
                </CardContent>
              </Card>
            </div>

            {/* Agents */}
            <Tabs defaultValue="all" className="space-y-4">
              <TabsList>
                <TabsTrigger value="all">Tous les agents</TabsTrigger>
                <TabsTrigger value="chains" data-testid="tab-chains">Chaînes</TabsTrigger>
                <TabsTrigger value="pipeline">Pipeline</TabsTrigger>
                <TabsTrigger value="produits">Produits & Prestations</TabsTrigger>
                <TabsTrigger value="directives">Directives</TabsTrigger>
                <TabsTrigger value="active">Actifs</TabsTrigger>
                <TabsTrigger value="rapports">Rapports</TabsTrigger>
                <TabsTrigger value="logs">Journal</TabsTrigger>
              </TabsList>

              <TabsContent value="all" className="space-y-3">
                {Object.entries(AGENT_META).map(([agentId, meta]) => {
                  const config = configs.find((c) => c.agent_id === agentId);
                  const stats = getAgentStats(agentId);
                  const agentStatus = getAgentStatus(agentId);
                  const status = statusConfig[agentStatus];
                  const StatusIcon = status.icon;
                  const AgentIcon = meta.icon;
                  const isRunning = runningAgent === agentId;
                  const lastLog = logs.find((l) => l.agent_id === agentId);

                  return (
                    <Card key={agentId} className={`border-border/50 transition-all ${!config?.enabled ? "opacity-50" : ""}`}>
                      <CardContent className="p-4 md:p-5">
                        <div className="flex flex-col md:flex-row md:items-start gap-4">
                          <div className="flex items-start gap-3 flex-1">
                            <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${meta.color}20` }}>
                              <AgentIcon className="h-5 w-5" style={{ color: meta.color }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="font-semibold text-foreground">{meta.name}</h3>
                                <Badge variant="outline" className={`text-[10px] ${status.className}`}>
                                  <StatusIcon className="h-2.5 w-2.5 mr-1" />
                                  {status.label}
                                </Badge>
                              </div>
                              <p className="text-xs text-primary font-medium">{meta.role}</p>
                              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{meta.description}</p>

                              {lastLog && (
                                <div className="mt-3 p-2 rounded-lg bg-muted/50 border border-border/50">
                                  <p className="text-xs text-foreground">{lastLog.action}</p>
                                  <p className="text-[10px] text-muted-foreground mt-0.5">
                                    {formatDistanceToNow(new Date(lastLog.created_at), { addSuffix: true, locale: fr })}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="flex md:flex-col items-center md:items-end gap-3">
                            <Switch checked={config?.enabled ?? false} onCheckedChange={() => toggleAgent(agentId)} />
                            {config?.enabled && ["analyste", "veilleur", "qualificateur", "redacteur", "nurturing"].includes(agentId) && (
                              <Button size="sm" variant="outline" onClick={() => handleRunAgent(agentId)} disabled={isRunning} className="gap-1.5">
                                {isRunning ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
                                Lancer
                              </Button>
                            )}
                            <div className="flex gap-4 text-xs text-muted-foreground">
                              <div className="text-center">
                                <p className="font-semibold text-foreground text-sm">{stats.completed}</p>
                                <p>terminées</p>
                              </div>
                              <div className="text-center">
                                <p className="font-semibold text-foreground text-sm">{stats.running}</p>
                                <p>en cours</p>
                              </div>
                              <div className="text-center">
                                <p className="font-semibold text-foreground text-sm">{stats.successRate > 0 ? `${stats.successRate.toFixed(0)}%` : "—"}</p>
                                <p>succès</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {stats.running > 0 && (
                          <div className="mt-3">
                            <Progress value={65} className="h-1" />
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </TabsContent>

              <TabsContent value="pipeline">
                <AcquisitionPipeline />
              </TabsContent>

              <TabsContent value="chains">
                <AgentChains />
              </TabsContent>

              <TabsContent value="produits">
                <AgentProducts />
              </TabsContent>

              <TabsContent value="directives">
                <AgentDirectives />
              </TabsContent>

              <TabsContent value="active" className="space-y-3">
                {Object.entries(AGENT_META)
                  .filter(([id]) => {
                    const s = getAgentStatus(id);
                    return s === "active" || s === "running";
                  })
                  .map(([agentId, meta]) => {
                    const agentStatus = getAgentStatus(agentId);
                    const status = statusConfig[agentStatus];
                    const StatusIcon = status.icon;
                    const AgentIcon = meta.icon;
                    const lastLog = logs.find((l) => l.agent_id === agentId);
                    return (
                      <Card key={agentId} className="border-border/50">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: `${meta.color}20` }}>
                              <AgentIcon className="h-4 w-4" style={{ color: meta.color }} />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h3 className="font-medium text-sm text-foreground">{meta.name}</h3>
                                <Badge variant="outline" className={`text-[10px] ${status.className}`}>
                                  <StatusIcon className="h-2.5 w-2.5 mr-1" />
                                  {status.label}
                                </Badge>
                              </div>
                              <p className="text-xs text-muted-foreground">{lastLog?.action || "En attente"}</p>
                            </div>
                            {lastLog && (
                              <p className="text-[10px] text-muted-foreground">
                                {formatDistanceToNow(new Date(lastLog.created_at), { addSuffix: true, locale: fr })}
                              </p>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
              </TabsContent>

              <TabsContent value="rapports" className="space-y-3">
                {latestReports.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" />
                    <p className="text-sm text-muted-foreground">Aucun rapport. Lancez les agents pour générer des rapports.</p>
                    <Button size="sm" className="mt-4 gap-1.5" onClick={handleRunAll} disabled={runningAll} data-testid="run-all-from-reports">
                      <Rocket className="h-4 w-4" /> Lancer tous les agents
                    </Button>
                  </div>
                ) : (
                  latestReports.map((report) => {
                    const meta = AGENT_META[report.agent_id];
                    if (!meta) return null;
                    const AgentIcon = meta.icon;
                    const briefing = report.output_data?.briefing || "Aucun rapport";
                    return (
                      <Card key={report.agent_id} className="border-border/50" data-testid={`report-${report.agent_id}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${meta.color}20` }}>
                              <AgentIcon className="h-4 w-4" style={{ color: meta.color }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="text-sm font-semibold text-foreground">{meta.name}</h3>
                                <Badge variant="outline" className="text-[10px]">{meta.role}</Badge>
                                {report.completed_at && (
                                  <span className="text-[10px] text-muted-foreground ml-auto">
                                    {formatDistanceToNow(new Date(report.completed_at), { addSuffix: true, locale: fr })}
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-white whitespace-pre-line bg-muted/30 rounded-lg p-3 border border-border/30">
                                {briefing}
                              </div>
                              <div className="flex gap-2 mt-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-[10px] h-7 gap-1"
                                  onClick={() => navigate("/emails")}
                                  data-testid={`report-emails-${report.agent_id}`}
                                >
                                  <Play className="h-3 w-3" /> Voir les emails
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-[10px] h-7 gap-1"
                                  onClick={() => navigate("/tunnel-vente")}
                                >
                                  <TrendingUp className="h-3 w-3" /> Tunnel de vente
                                </Button>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </TabsContent>

              <TabsContent value="logs" className="space-y-2">
                {logs.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground text-sm">
                    Aucune activité enregistrée. Lancez un agent pour commencer.
                  </div>
                ) : (
                  logs.map((log, idx) => (
                    <div key={`${log.agent_id}-${log.created_at}-${idx}`} className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 border border-border/30">
                      {log.log_type === "warning" ? (
                        <AlertTriangle className="h-3.5 w-3.5 text-amber-500 mt-0.5 shrink-0" />
                      ) : log.log_type === "success" ? (
                        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mt-0.5 shrink-0" />
                      ) : (
                        <Activity className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-foreground capitalize">{AGENT_META[log.agent_id]?.name || log.agent_id}</span>
                          <span className="text-[10px] text-muted-foreground">
                            {formatDistanceToNow(new Date(log.created_at), { addSuffix: true, locale: fr })}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground">{log.action}</p>
                      </div>
                    </div>
                  ))
                )}
              </TabsContent>
            </Tabs>
          </main>
        </div>
      </div>

      <NanoCorpBriefingDialog open={briefingOpen} onOpenChange={setBriefingOpen} content={briefingContent} />
    </SidebarProvider>
  );
};

export default NanoCorp;
